import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GetSupportedPispInstitutions } from '@app/finxone-web-frontend/app/lib/actions/savings-account.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { SavingsAccountService } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.service';
import { SavingsAccountState } from '@app/finxone-web-frontend/app/lib/state/savings-account.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  ActiveDevice,
  baseCssConfigStyle,
  BaseCssProperties,
  BaseWidgetProperties,
  commonContentStyleList,
  PISPDepositStatusEnum,
  ProviderClass,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import {
  catchError,
  filter,
  interval,
  map,
  Observable,
  of,
  Subject,
  startWith,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs';
import { BaseComponent } from '../base/base.component';
import { redirectUserToDepositStatusPage } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/savings-account-actions/pisp-deposit.utils';
import { RoleState, RoleStateModel } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { UpdateFormDataAction } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-pisp-check-billing-request-loader-widget',
  templateUrl: './pisp-check-billing-request-loader-widget.component.html',
  styleUrls: ['./pisp-check-billing-request-loader-widget.component.scss'],
})
export class PispCheckBillingRequestLoaderComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public logoUrl = '';

  @Select(RoleState.getRole)
  roles!: Observable<RoleStateModel>;
  roleInfo: RoleStateModel;
  public widgetConfig: BaseWidgetProperties;

  public dataIn: ProviderClass<any> = inject(ProviderClass);
  public store: Store = inject(Store);
  public configService: ConfigService = inject(ConfigService);
  public router: Router = inject(Router);
  public deviceDetector = inject(DeviceDetectorService);
  public savingsAccountService: SavingsAccountService = inject(SavingsAccountService);
  size: baseCssConfigStyle;
  margin: baseCssConfigStyle;
  spinnerSize: baseCssConfigStyle;

  constructor() {
    super();
    this.roles.pipe(takeUntil(this.destroy$)).subscribe((roles) => {
      this.roleInfo = roles;
    });

    const data = this.store.selectSnapshot(SavingsAccountState.getAccounts);

    this.store
      .select(SavingsAccountState.getNominatedAccountDetails)
      .pipe(
        switchMap((nominatedAccountDetails) => {
          return this.store.select(SavingsAccountState.getPispSupportedInstitutions).pipe(
            map((pispSupportedInstitutions) => {
              if (pispSupportedInstitutions?.length === 0) {
                const country = this.store.selectSnapshot(ProfileState.getProfile)?.country;
                this.store.dispatch(new GetSupportedPispInstitutions(country));
              }

              const institution = pispSupportedInstitutions?.find(
                (institution) => institution.id === nominatedAccountDetails?.pispInstitutionId,
              );
              return institution;
            }),
          );
        }),
        map((pispInstitution) => {
          return pispInstitution ?? null;
        }),
      )
      .subscribe((pispInstitution) => {
        if (pispInstitution) {
          const logo = pispInstitution.logo_url;
          this.logoUrl = logo;
        }
      });

    const billingRequestId = data?.pispDepositInitiateResponse?.id;

    if (billingRequestId) {
      const activeAccountId = this.store.selectSnapshot(SavingsAccountState.getCurrentAccountId);
      interval(5000)
        .pipe(
          startWith(0),
          takeUntil(this.destroy$),
          switchMap(() =>
            this.savingsAccountService.checkPispDepositStatus(billingRequestId, activeAccountId).pipe(
              catchError((err) => {
                console.error(err);
                return of(null);
              }),
            ),
          ),
          filter((res): res is { status: PISPDepositStatusEnum } => !!res),
          tap((res) => {
            if (
              res.status === PISPDepositStatusEnum.COMPLETED ||
              res.status === PISPDepositStatusEnum.FAILED
            ) {
              // update form state with the status of the deposit
              this.store.dispatch(new UpdateFormDataAction({ formData: { pispDepositStatus: res.status } }));
              // use util method from action button to redirect the user
              redirectUserToDepositStatusPage(
                res.status,
                this.store,
                this.router,
                this.deviceDetector.deviceType as ActiveDevice,
              );
            }
          }),
          takeWhile((res) => res.status !== PISPDepositStatusEnum.COMPLETED, true),
        )
        .subscribe();
    }
  }

  ngOnInit(): void {
    if (this.dataIn?.attri) {
      this.widgetConfig = this.dataIn.attri.widgetProperties;
    }
    this.getWidgetCssConfig();
  }

  public getWidgetCssConfig() {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonContentStyleList,
    );
    this.spinnerSize = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.LEFT_ICON_SIZE,
    ]);
    this.size = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.RIGHT_ICON_SIZE,
    ]);
    this.margin = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [BaseCssProperties.MARGIN]);
  }
}
